import {
  ErrorCode,
  type Page,
  type ServiceAgreementDecideInput,
  type ServiceAgreementInput,
  type ServiceAvailability,
  type ServiceBoard,
  type ServiceBoardQuery,
  type ServiceCollection,
  type ServiceCollectionInput,
  type ServiceCollectionListQuery,
  type ServiceCollectionVerifyInput,
  type ServiceDashboard,
  type ServiceFollowUp,
  type ServiceFollowUpApproveInput,
  type ServiceFollowUpInput,
  type ServiceFollowUpListQuery,
  type ServiceFollowUpResubmitInput,
  type ServiceInvoiceInput,
  type ServiceInvoiceResult,
  type ServiceItemInput,
  type ServiceItemUpdate,
  type ServiceJobCounts,
  type ServiceJobCreateInput,
  type ServiceJobDetail,
  type ServiceJobListItem,
  type ServiceJobListQuery,
  type ServiceJobUpdateInput,
  type ServiceMyDay,
  type ServiceSite,
  type ServiceSiteInput,
  type ServiceSiteUpdate,
  type ServiceTeam,
  type ServiceUsualItem,
  type ServiceVisitDetail,
  type ServiceVisitFinishInput,
  type ServiceVisitListQuery,
  type ServiceVisitReportInput,
  type ServiceVisitRescheduleInput,
  type ServiceVisitScheduleInput,
  type ServiceVisitSummary,
  type ServiceVisitWorkInput,
  type ServiceWaiting,
} from '@sahab/contracts'
import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { api, isApiError, type QueryValue } from '../../lib/api'

/**
 * Service jobs and visits (SERVICE.md). Every read lives under ['service'], so one invalidation after a
 * command refreshes the register, the board, the dashboard, the queues and the open pages.
 */
export const serviceKeys = {
  all: ['service'] as const,
  jobs: (query: ServiceJobListQuery) => [...serviceKeys.all, 'jobs', query] as const,
  counts: (query: object) => [...serviceKeys.all, 'counts', query] as const,
  job: (id: string) => [...serviceKeys.all, 'job', id] as const,
  visit: (id: string) => [...serviceKeys.all, 'visit', id] as const,
  visits: (query: ServiceVisitListQuery) => [...serviceKeys.all, 'visits', query] as const,
  board: (query: ServiceBoardQuery) => [...serviceKeys.all, 'board', query] as const,
  dashboard: () => [...serviceKeys.all, 'dashboard'] as const,
  myDay: () => [...serviceKeys.all, 'my-day'] as const,
  teams: () => [...serviceKeys.all, 'teams'] as const,
  availability: (q: object) => [...serviceKeys.all, 'availability', q] as const,
  followUps: (query: ServiceFollowUpListQuery) => [...serviceKeys.all, 'follow-ups', query] as const,
  collections: (query: ServiceCollectionListQuery) => [...serviceKeys.all, 'collections', query] as const,
  sites: (customerId: string, archived: boolean) =>
    [...serviceKeys.all, 'sites', customerId, archived] as const,
  usualItems: (jobId: string) => [...serviceKeys.all, 'usual-items', jobId] as const,
}

/** Drops undefined and empty keys, so equal filters share one cache entry and one query string. */
function params(query: object): Record<string, QueryValue> {
  return Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== '')) as Record<
    string,
    QueryValue
  >
}

// ── reads ────────────────────────────────────────────────────────────────────

export function serviceJobsQueryOptions(query: ServiceJobListQuery) {
  return queryOptions({
    queryKey: serviceKeys.jobs(query),
    queryFn: ({ signal }) =>
      api.get<Page<ServiceJobListItem>>('/service/jobs', { signal, query: params(query) }),
    placeholderData: keepPreviousData,
  })
}

/** The register's tab counts for the same filters (view, page and sort left out). */
export function serviceJobCountsQueryOptions(query: ServiceJobListQuery) {
  const { view: _view, page: _page, pageSize: _size, sort: _sort, ...filters } = query
  return queryOptions({
    queryKey: serviceKeys.counts(filters),
    queryFn: ({ signal }) =>
      api.get<ServiceJobCounts>('/service/jobs/counts', { signal, query: params(filters) }),
    placeholderData: keepPreviousData,
  })
}

export function serviceJobQueryOptions(id: string) {
  return queryOptions({
    queryKey: serviceKeys.job(id),
    queryFn: ({ signal }) => api.get<ServiceJobDetail>(`/service/jobs/${id}`, { signal }),
  })
}

export function serviceVisitQueryOptions(id: string) {
  return queryOptions({
    queryKey: serviceKeys.visit(id),
    queryFn: ({ signal }) => api.get<ServiceVisitDetail>(`/service/visits/${id}`, { signal }),
  })
}

export function serviceVisitsQueryOptions(query: ServiceVisitListQuery) {
  return queryOptions({
    queryKey: serviceKeys.visits(query),
    queryFn: ({ signal }) =>
      api.get<ServiceVisitSummary[]>('/service/visits', { signal, query: params(query) }),
    placeholderData: keepPreviousData,
  })
}

export function serviceBoardQueryOptions(query: ServiceBoardQuery) {
  return queryOptions({
    queryKey: serviceKeys.board(query),
    queryFn: ({ signal }) => api.get<ServiceBoard>('/service/board', { signal, query: params(query) }),
    placeholderData: keepPreviousData,
  })
}

export const serviceDashboardQueryOptions = queryOptions({
  queryKey: serviceKeys.dashboard(),
  queryFn: ({ signal }) => api.get<ServiceDashboard>('/service/dashboard', { signal }),
})

/**
 * What a job usually needs (its site's, its customer's and the company's items, at the price to charge),
 * for the one-tap "Usual items" on the visit and the job page. Refreshed with everything under
 * ['service'], so an item just added leaves the list.
 */
export function serviceUsualItemsQueryOptions(jobId: string) {
  return queryOptions({
    queryKey: serviceKeys.usualItems(jobId),
    queryFn: ({ signal }) => api.get<ServiceUsualItem[]>(`/service/jobs/${jobId}/usual-items`, { signal }),
    staleTime: 60_000,
  })
}

/** My work's service part: today's visits of my teams and the service decisions waiting for me. */
export const serviceMyDayQueryOptions = queryOptions({
  queryKey: serviceKeys.myDay(),
  queryFn: ({ signal }) => api.get<ServiceMyDay>('/service/my-day', { signal }),
})

/** Active teams with their members: the scheduling pickers and the board's rows. */
export const serviceTeamsQueryOptions = queryOptions({
  queryKey: serviceKeys.teams(),
  queryFn: ({ signal }) => api.get<ServiceTeam[]>('/service/teams', { signal }),
  staleTime: 60_000,
})

/** The team's and the lead's other visits that day (the scheduling dialog's availability). */
export function serviceAvailabilityQueryOptions(q: {
  date: string
  teamId: string
  leadId?: string
  visitId?: string
}) {
  return queryOptions({
    queryKey: serviceKeys.availability(q),
    queryFn: ({ signal }) =>
      api.get<ServiceAvailability>('/service/availability', { signal, query: params(q) }),
  })
}

export function serviceFollowUpsQueryOptions(query: ServiceFollowUpListQuery) {
  return queryOptions({
    queryKey: serviceKeys.followUps(query),
    queryFn: ({ signal }) =>
      api.get<Page<ServiceFollowUp>>('/service/follow-ups', { signal, query: params(query) }),
    placeholderData: keepPreviousData,
  })
}

export function serviceCollectionsQueryOptions(query: ServiceCollectionListQuery) {
  return queryOptions({
    queryKey: serviceKeys.collections(query),
    queryFn: ({ signal }) =>
      api.get<Page<ServiceCollection>>('/service/collections', { signal, query: params(query) }),
    placeholderData: keepPreviousData,
  })
}

export function serviceSitesQueryOptions(customerId: string, archived = false) {
  return queryOptions({
    queryKey: serviceKeys.sites(customerId, archived),
    queryFn: ({ signal }) =>
      api.get<ServiceSite[]>('/service/sites', { signal, query: params({ customerId, archived }) }),
  })
}

// ── commands ─────────────────────────────────────────────────────────────────

/**
 * After any service command: the answer replaces its detail in the cache and everything else under
 * ['service'] refreshes, with the sales documents it may have made and the notification bell.
 */
export function settleService(
  client: QueryClient,
  result?: { kind: 'job'; job: ServiceJobDetail } | { kind: 'visit'; visit: ServiceVisitDetail },
): void {
  if (result?.kind === 'job') client.setQueryData(serviceKeys.job(result.job.id), result.job)
  if (result?.kind === 'visit') client.setQueryData(serviceKeys.visit(result.visit.id), result.visit)
  void client.invalidateQueries({ queryKey: serviceKeys.all })
  void client.invalidateQueries({ queryKey: ['notifications'] })
  void client.invalidateQueries({ queryKey: ['sales'] })
  void client.invalidateQueries({ queryKey: ['customers'] })
}

/**
 * A command refused with 409 VERSION_CONFLICT: someone else changed the record meanwhile. Everything under
 * ['service'] reloads, so the open dialog (kept open, its input kept) sends the latest version on retry.
 */
export function isStaleVersion(error: unknown): boolean {
  return isApiError(error) && error.code === ErrorCode.VERSION_CONFLICT
}

function reloadIfStale(client: QueryClient, error: unknown): void {
  if (isStaleVersion(error)) void client.invalidateQueries({ queryKey: serviceKeys.all })
}

function useJobMutation<V>(run: (vars: V) => Promise<ServiceJobDetail>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: run,
    onSuccess: (job) => settleService(client, { kind: 'job', job }),
    onError: (error) => reloadIfStale(client, error),
  })
}

function useVisitMutation<V>(run: (vars: V) => Promise<ServiceVisitDetail>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: run,
    onSuccess: (visit) => settleService(client, { kind: 'visit', visit }),
    onError: (error) => reloadIfStale(client, error),
  })
}

function useServiceMutation<V, R>(run: (vars: V) => Promise<R>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: run,
    onSuccess: () => settleService(client),
    onError: (error) => reloadIfStale(client, error),
  })
}

export function useCreateServiceJob() {
  return useJobMutation((input: ServiceJobCreateInput) => api.post<ServiceJobDetail>('/service/jobs', input))
}

export function useUpdateServiceJob(id: string) {
  return useJobMutation((input: ServiceJobUpdateInput) =>
    api.patch<ServiceJobDetail>(`/service/jobs/${id}`, input),
  )
}

export function useCancelServiceJob(id: string) {
  return useJobMutation((input: { version: number; reason: string }) =>
    api.post<ServiceJobDetail>(`/service/jobs/${id}/cancel`, input),
  )
}

export function useSetJobWaiting(id: string) {
  return useJobMutation(
    (input: { version: number; waitingFor: ServiceWaiting | null; note?: string | null }) =>
      api.post<ServiceJobDetail>(`/service/jobs/${id}/waiting`, input),
  )
}

export function useAddServiceItem(jobId: string) {
  return useJobMutation((input: ServiceItemInput) =>
    api.post<ServiceJobDetail>(`/service/jobs/${jobId}/items`, input),
  )
}

export function useUpdateServiceItem(jobId: string) {
  return useJobMutation(({ itemId, ...input }: ServiceItemUpdate & { itemId: string }) =>
    api.patch<ServiceJobDetail>(`/service/jobs/${jobId}/items/${itemId}`, input),
  )
}

export function useRemoveServiceItem(jobId: string) {
  return useJobMutation(({ itemId, version }: { itemId: string; version: number }) =>
    api.post<ServiceJobDetail>(`/service/jobs/${jobId}/items/${itemId}/remove`, { version }),
  )
}

/** Records a price approval (accepted, declined, or sent and awaiting the customer). */
export function useRecordAgreement(jobId: string) {
  return useJobMutation((input: ServiceAgreementInput) =>
    api.post<ServiceJobDetail>(`/service/jobs/${jobId}/agreements`, input),
  )
}

export function useDecideAgreement() {
  return useJobMutation(({ agreementId, ...input }: ServiceAgreementDecideInput & { agreementId: string }) =>
    api.post<ServiceJobDetail>(`/service/agreements/${agreementId}/decide`, input),
  )
}

export function useScheduleVisit(jobId: string) {
  return useVisitMutation((input: ServiceVisitScheduleInput) =>
    api.post<ServiceVisitDetail>(`/service/jobs/${jobId}/visits`, input),
  )
}

export function useRescheduleVisit(visitId: string) {
  return useVisitMutation((input: ServiceVisitRescheduleInput) =>
    api.post<ServiceVisitDetail>(`/service/visits/${visitId}/reschedule`, input),
  )
}

export function useCancelVisit(visitId: string) {
  return useVisitMutation((input: { version: number; reason?: string }) =>
    api.post<ServiceVisitDetail>(`/service/visits/${visitId}/cancel`, input),
  )
}

/** The technician's steps: start (on site) and begin work. */
export function useVisitStep(visitId: string) {
  return useVisitMutation(({ step, version }: { step: 'start' | 'begin-work'; version: number }) =>
    api.post<ServiceVisitDetail>(`/service/visits/${visitId}/${step}`, { version }),
  )
}

export function useSaveVisitWork(visitId: string) {
  return useVisitMutation((input: ServiceVisitWorkInput) =>
    api.post<ServiceVisitDetail>(`/service/visits/${visitId}/work`, input),
  )
}

export function useSaveVisitReport(visitId: string) {
  return useVisitMutation((input: ServiceVisitReportInput) =>
    api.patch<ServiceVisitDetail>(`/service/visits/${visitId}/report`, input),
  )
}

export function useFinishVisit(visitId: string) {
  return useVisitMutation((input: ServiceVisitFinishInput) =>
    api.post<ServiceVisitDetail>(`/service/visits/${visitId}/finish`, input),
  )
}

export function useRequestFollowUp(visitId: string) {
  return useServiceMutation((input: ServiceFollowUpInput) =>
    api.post<ServiceFollowUp>(`/service/visits/${visitId}/follow-ups`, input),
  )
}

export function useApproveFollowUp(id: string) {
  return useServiceMutation((input: ServiceFollowUpApproveInput) =>
    api.post<ServiceFollowUp>(`/service/follow-ups/${id}/approve`, input),
  )
}

/** The manager's one tap: the follow-up booked as the technician proposed it. */
export function useApproveFollowUpAsProposed(id: string) {
  return useServiceMutation((input: { version: number }) =>
    api.post<ServiceFollowUp>(`/service/follow-ups/${id}/approve-as-proposed`, input),
  )
}

export function useReturnFollowUp(id: string) {
  return useServiceMutation((input: { version: number; note: string }) =>
    api.post<ServiceFollowUp>(`/service/follow-ups/${id}/return`, input),
  )
}

export function useResubmitFollowUp(id: string) {
  return useServiceMutation((input: ServiceFollowUpResubmitInput) =>
    api.post<ServiceFollowUp>(`/service/follow-ups/${id}/resubmit`, input),
  )
}

export function useWithdrawFollowUp(id: string) {
  return useServiceMutation((input: { version: number }) =>
    api.post<ServiceFollowUp>(`/service/follow-ups/${id}/withdraw`, input),
  )
}

/** Invoices completed items. Send the same `requestId` again to retry safely (no second invoice). */
export function useInvoiceServiceJob(jobId: string) {
  return useServiceMutation((input: ServiceInvoiceInput) =>
    api.post<ServiceInvoiceResult>(`/service/jobs/${jobId}/invoices`, input),
  )
}

/** Reports money collected. The same `requestId` again returns the first report. */
export function useRecordCollection(jobId: string) {
  return useServiceMutation((input: ServiceCollectionInput) =>
    api.post<ServiceCollection>(`/service/jobs/${jobId}/collections`, input),
  )
}

/** Verifies a collection; one by cheque also enters the cheque register (ADR-046), so it refreshes too. */
export function useVerifyCollection(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: ServiceCollectionVerifyInput) =>
      api.post<ServiceCollection>(`/service/collections/${id}/verify`, input),
    onSuccess: () => {
      settleService(client)
      void client.invalidateQueries({ queryKey: ['cheques'] })
    },
    onError: (error) => reloadIfStale(client, error),
  })
}

export function useRejectCollection(id: string) {
  return useServiceMutation((input: { version: number; reason: string }) =>
    api.post<ServiceCollection>(`/service/collections/${id}/reject`, input),
  )
}

export function useCreateServiceSite() {
  return useServiceMutation((input: ServiceSiteInput) => api.post<ServiceSite>('/service/sites', input))
}

export function useUpdateServiceSite(id: string) {
  return useServiceMutation((input: ServiceSiteUpdate) =>
    api.patch<ServiceSite>(`/service/sites/${id}`, input),
  )
}

export function useArchiveServiceSite(id: string) {
  return useServiceMutation(({ archived, version }: { archived: boolean; version: number }) =>
    api.post<ServiceSite>(`/service/sites/${id}/${archived ? 'archive' : 'restore'}`, { version }),
  )
}

/** New job › "Book a visit" with nothing left to choose: the visit of a job just created. */
export function useBookVisit() {
  return useVisitMutation(({ jobId, ...input }: ServiceVisitScheduleInput & { jobId: string }) =>
    api.post<ServiceVisitDetail>(`/service/jobs/${jobId}/visits`, input),
  )
}
